import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import {
  VISIBLE_COMPANY_TYPES,
  resolveVisibleCompanyLabel,
  normalizeCompanyTypeForSave,
  getRoleFamilies,
  normalizeRoleFamilyForSave,
  SENIORITY_OPTIONS,
  getOrderedCapabilities,
} from "@/lib/intake-data";
import AccountFollowList from "@/components/AccountFollowList";
import { Button } from "@/components/ui/button";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";
import { toast } from "sonner";
import { Lock } from "lucide-react";

function OrgChartSelector({ selected, onSelect, testIdPrefix }) {
  return (
    <div className="flex flex-col items-center gap-0 w-full max-w-sm mx-auto">
      {SENIORITY_OPTIONS.map((opt, i) => (
        <div key={opt.value} className="flex flex-col items-center w-full">
          {i > 0 && <div className="w-px h-5 bg-zinc-200" />}
          <button
            type="button"
            onClick={() => onSelect(opt.value)}
            aria-pressed={selected === opt.value}
            className={`w-full p-3 border-2 transition-all text-center ${
              selected === opt.value
                ? "border-amber-700 bg-amber-700 text-white"
                : "border-zinc-200 bg-white hover:border-zinc-300 text-zinc-950"
            }`}
            data-testid={`${testIdPrefix}-${opt.value}`}
          >
            <span className="text-sm font-medium block">{opt.label}</span>
            <span className={`block mt-0.5 ${selected === opt.value ? "text-white/70" : "text-zinc-900"}`} style={{ fontSize: "12px" }}>
              {opt.desc}
            </span>
          </button>
        </div>
      ))}
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companyType, setCompanyType] = useState("");
  const [roleFamily, setRoleFamily] = useState("");
  const [seniorityTier, setSeniorityTier] = useState("");
  const [capabilityBets, setCapabilityBets] = useState([]);
  const [followedAccounts, setFollowedAccounts] = useState([]);
  const [accountsCatalog, setAccountsCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  useEffect(() => {
    if (user === null) { navigate("/signin"); return; }
    if (user === undefined) return;
    Promise.all([
      API.get("/intake/responses"),
      API.get("/accounts"),
    ]).then(([intakeRes, accountsRes]) => {
      const data = intakeRes.data;
      setCompanyType(data.company_type || "");
      // Wave 3m — legacy drift: pre-select `retention` for users whose saved
      // role_family is `leadership` (no longer offered in the UI). On save
      // we rewrite it on the wire too.
      setRoleFamily(data.role_family === "leadership" ? "retention" : (data.role_family || ""));
      setSeniorityTier(data.seniority_tier || "");
      setCapabilityBets(data.capability_bets || []);
      setFollowedAccounts(data.followed_accounts || []);
      setAccountsCatalog(accountsRes.data.accounts || []);
      setInitialSnapshot({
        role_family: data.role_family === "leadership" ? "retention" : (data.role_family || ""),
        seniority_tier: data.seniority_tier || "",
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user, navigate]);

  const roleFamilies = getRoleFamilies(companyType);
  const orderedCaps = getOrderedCapabilities(companyType);
  const lockedCompanyLabel = resolveVisibleCompanyLabel(companyType);

  const toggleCap = (val) => {
    setCapabilityBets((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : prev.length < 3 ? [...prev, val] : prev
    );
  };

  // Admin users bypass the ≤3 cap and the live-required check on followed
  // accounts so the team can dogfood any account in any state. Mirrors the
  // backend bypass in `validate_followed_accounts(..., is_admin=True)`.
  const isAdmin = !!user?.is_admin;
  const followedCap = isAdmin ? Infinity : 3;

  const toggleFollowedAccount = (id) => {
    setFollowedAccounts((prev) => {
      if (prev.includes(id)) {
        return prev.filter((v) => v !== id);
      }
      if (prev.length >= followedCap) return prev;
      return [...prev, id];
    });
  };

  const handleMakeDefault = (id) => {
    setFollowedAccounts((prev) => {
      if (!prev.includes(id)) return prev;
      return [id, ...prev.filter((v) => v !== id)];
    });
  };

  const hasLiveSelected = accountsCatalog.some((a) => a.status === "live" && followedAccounts.includes(a.id));
  const hasAnySelected = followedAccounts.length >= 1;

  const handleSave = async () => {
    // Wave 3k — full Reveal-setup validation before hitting the API.
    if (!companyType) {
      toast.error("Pick your Kalib AI lens.");
      return;
    }
    if (!hasAnySelected) {
      toast.error("Choose at least one account to follow.");
      return;
    }
    if (!isAdmin && !hasLiveSelected) {
      toast.error("Select at least one account that is available now.");
      return;
    }
    if (!isAdmin && followedAccounts.length > 3) {
      toast.error("You can follow up to 3 accounts.");
      return;
    }
    if (!roleFamily) {
      toast.error("Pick what you're trying to move.");
      return;
    }
    if (!seniorityTier) {
      toast.error("Pick how you operate.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        // Wave 3m — silent legacy drift: a user carrying `leadership` gets
        // rewritten to `retention` on next save. No migration script.
        role_family: normalizeRoleFamilyForSave(roleFamily),
        seniority_tier: seniorityTier || null,
        capability_bets: capabilityBets.length > 0 ? capabilityBets : null,
        followed_accounts: followedAccounts,
      };
      // Wave 6c (2026-04-25) — admins can switch their vendor lens from this
      // page (the dropdown is unlocked above for admin users). Persist the
      // change so the next Reveal calibrates correctly.
      // Regular users: legacy `technology_operations` is silently rewritten
      // to `services` on next save (no migration script).
      if (user?.is_admin) {
        payload.company_type = normalizeCompanyTypeForSave(companyType);
      } else if (companyType === "technology_operations") {
        payload.company_type = normalizeCompanyTypeForSave(companyType);
      }
      await API.put("/settings/profile", payload);

      const changed = [];
      if (initialSnapshot && initialSnapshot.role_family !== roleFamily) changed.push("role_family");
      if (initialSnapshot && initialSnapshot.seniority_tier !== (seniorityTier || "")) changed.push("seniority_tier");
      if (changed.length) {
        try {
          sessionStorage.setItem("kalib_recalib_event", JSON.stringify({
            timestamp: new Date().toISOString(),
            changed,
            prev: {
              role_family: initialSnapshot.role_family,
              seniority_tier: initialSnapshot.seniority_tier,
            },
            next: {
              role_family: roleFamily,
              seniority_tier: seniorityTier || "",
            },
          }));
        } catch {
          // sessionStorage disabled — skip
        }
      }

      sessionStorage.removeItem("kalib_reveal_seen");
      navigate("/intelligence");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (user === undefined || loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <p className="text-sm text-zinc-900">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="settings-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/" className="block" data-testid="settings-logo-link" aria-label="Kalib AI home">
              <KalibLogo variant="light" height={28} testId="settings-header-logo" />
            </Link>
            <BetaBadge />
          </div>
          <div className="flex items-center gap-5">
            <Link to="/settings/account" className="text-sm text-zinc-900 hover:text-zinc-950 transition-colors" data-testid="settings-account-link">
              Account settings
            </Link>
            <Link to="/intelligence" className="text-sm text-zinc-900 hover:text-zinc-950 transition-colors" data-testid="back-to-intelligence-link">
              Back to Reveal
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 font-heading mb-2" data-testid="settings-page-title">
          Reveal setup
        </h1>
        <p className="text-sm text-zinc-900 mb-10" data-testid="settings-page-subtitle">
          Change the combination Kalib AI uses to calibrate your Reveal.
        </p>

        {/* ─── SECTION A — Reveal setup ──────────────────────────────────── */}
        <section className="space-y-10" data-testid="settings-reveal-setup-section">

          {/* 1. company_type — "Your Kalib AI lens" */}
          <div data-testid="settings-company-block">
            <h2 className="text-base font-medium text-zinc-950 font-heading">Your Kalib AI lens</h2>
            <p className="text-[13px] text-zinc-900 mt-1">
              Choose the vendor lens that best matches how you sell into accounts. This stays fixed for your subscription.
            </p>
            <p className="text-[11px] text-zinc-700 mt-1 italic">
              Pick the lens that best matches what you primarily sell into the account, not your firm's legal category.
            </p>

            {/* Single-select. Locked-once-saved for regular users; admins can
                switch freely (backend bypass already in place). */}
            <div className="mt-4">
              {companyType && !user?.is_admin ? (
                <div
                  className="bg-zinc-50 border border-zinc-200 px-3 py-2.5 flex items-center justify-between gap-4"
                  data-testid="settings-company-locked"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Lock className="w-3.5 h-3.5 text-zinc-700 flex-shrink-0" strokeWidth={1.75} />
                    <span className="text-sm font-medium text-zinc-950 truncate" data-testid="settings-company-label">
                      {lockedCompanyLabel}
                    </span>
                    <span className="text-[11px] uppercase tracking-widest text-zinc-900 border border-zinc-300 px-1.5 py-0.5 flex-shrink-0">
                      Locked to your subscription
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  {user?.is_admin && companyType && (
                    <p className="text-[11px] uppercase tracking-widest text-amber-700 font-medium mb-2" data-testid="settings-admin-unlock-note">
                      Admin — vendor lens unlocked for testing
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="settings-company-picker">
                    {VISIBLE_COMPANY_TYPES.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setCompanyType(opt.value)}
                        className={`text-left p-3 border transition-all ${
                          companyType === opt.value
                            ? "border-amber-700 bg-amber-50 ring-1 ring-amber-700"
                            : "border-zinc-200 bg-white hover:border-zinc-300"
                        }`}
                        data-testid={`settings-company-${opt.value}`}
                      >
                        <span className="text-sm font-medium text-zinc-950 block">{opt.label}</span>
                        <span className="block mt-0.5 text-[11px] text-zinc-900">{opt.helper}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
              <p className="text-[12px] text-zinc-900 mt-2 leading-relaxed">
                Your vendor type is locked for your subscription — one subscriber, one vendor lens.
                {" "}
                <a href="mailto:hello@kalib.ai?subject=Change%20vendor%20type" className="text-amber-700 hover:text-amber-800 underline" data-testid="settings-company-contact-link">
                  Contact us to change
                </a>.
              </p>
            </div>
          </div>

          {/* 2. followed_accounts — "Accounts you follow" */}
          <div data-testid="settings-followed-accounts">
            <h2 className="text-base font-medium text-zinc-950 font-heading">Accounts you follow</h2>
            <p className="text-[13px] text-zinc-900 mt-1">
              Choose up to 3 accounts. Pick the ones you actively sell into or want to break into.
            </p>
            <p className="text-[12px] text-zinc-700 mt-1">
              One account opens as your default Reveal.
            </p>

            <div className="mt-4">
              <AccountFollowList
                accounts={accountsCatalog}
                selected={followedAccounts}
                onToggle={toggleFollowedAccount}
                onMakeDefault={handleMakeDefault}
                maxSelected={isAdmin ? 999 : 3}
                testIdPrefix="settings"
              />
              {!hasAnySelected && accountsCatalog.length > 0 && (
                <p className="text-xs text-red-600 mt-2" data-testid="settings-followed-warning-min">
                  Choose at least one account to follow.
                </p>
              )}
              {!isAdmin && hasAnySelected && !hasLiveSelected && (
                <p className="text-xs text-red-600 mt-2" data-testid="settings-followed-warning">
                  Select at least one account that is available now.
                </p>
              )}
            </div>
          </div>

          {/* 3. role_family — What you are trying to move */}
          <div data-testid="settings-role-block">
            <h2 className="text-base font-medium text-zinc-950 font-heading">What you are trying to move</h2>
            <p className="text-[13px] text-zinc-900 mt-1 mb-3">
              Choose the one commercial motion you most want Kalib AI to help with right now.
            </p>
            {!companyType ? (
              <div className="border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-900">Select your Kalib AI lens first</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {roleFamilies.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setRoleFamily(opt.value)}
                    className={`text-left p-3 border transition-all text-sm animate-fade-in ${
                      roleFamily === opt.value
                        ? "border-amber-700 bg-amber-50 ring-1 ring-amber-700 font-medium text-zinc-950"
                        : "border-zinc-200 bg-white hover:border-zinc-300 text-zinc-700"
                    }`}
                    data-testid={`settings-role-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 4. seniority_tier — How you operate */}
          <div data-testid="settings-seniority-block">
            <h2 className="text-base font-medium text-zinc-950 font-heading">How you operate</h2>
            <p className="text-[13px] text-zinc-900 mt-1 mb-3">
              Choose the scope you mostly operate at. Titles vary by company.
            </p>
            <OrgChartSelector selected={seniorityTier} onSelect={setSeniorityTier} testIdPrefix="settings-seniority" />
          </div>
        </section>

        {/* ─── CTAs ───────────────────────────────────────────────────────── */}
        <div className="mt-10 flex items-center gap-3" data-testid="settings-cta-row">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium px-6 py-2 h-auto"
            data-testid="settings-save-primary-btn"
          >
            {saving ? "Saving…" : "See my Reveal"}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="outline"
            className="rounded-none border-zinc-300 text-zinc-800 hover:bg-zinc-50 text-sm font-medium px-5 py-2 h-auto"
            data-testid="settings-save-secondary-btn"
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
